import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Camera, ArrowRight, Download, Heart, Clock } from "lucide-react";
import api from "@/lib/api";
import ShareBar from "@/components/ShareBar";

const toUrl = (p) => (p && p.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);

export default function PupOfTheMonth() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/pup/winner").then(({ data }) => setData(data)).catch(() => setData(null)); }, []);
  if (!data) return null;
  const w = data.winner;
  return (
    <section id="pup-of-the-month" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="pup-section">
      <div className="rounded-[2.5rem] bg-[#19324A] text-white p-6 sm:p-10 relative overflow-hidden">
        <span className="absolute -right-8 -top-8 text-[9rem] opacity-10 select-none" aria-hidden="true">🏆</span>
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center relative">
          {w ? (
            <div className="relative" data-testid="pup-winner">
              <img src={toUrl(w.photo)} alt={w.pup_name} className="w-full aspect-square object-cover rounded-[2rem] border-4 border-[#FFD93D] soft-shadow" data-testid="pup-winner-photo" />
              <span className="absolute -top-3 -left-3 rotate-[-6deg] bg-[#FFD93D] text-[#19324A] font-display font-extrabold text-sm px-4 py-2 rounded-full soft-shadow inline-flex items-center gap-1.5"><Trophy className="w-4 h-4" /> Pup of the Month</span>
            </div>
          ) : (
            <div className="aspect-square rounded-[2rem] border-4 border-dashed border-white/30 flex flex-col items-center justify-center text-center p-8" data-testid="pup-winner-empty">
              <Camera className="w-12 h-12 text-[#FFD93D] mb-3" />
              <p className="font-display font-extrabold text-2xl" style={{ color: "#fff" }}>This spot is up for grabs</p>
              <p className="text-white/70 text-sm mt-1">Be the first Bulldog to win {data.month_label}'s crown.</p>
            </div>
          )}
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFD93D]">{w ? w.month_label : data.month_label} · Community</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-2 leading-tight" style={{ color: "#fff" }} data-testid="pup-winner-title">
              {w ? <>Meet {w.pup_name} 🏆</> : <>Pup of the Month</>}
            </h2>
            {w ? (
              <>
                {w.caption && <p className="mt-3 text-lg text-white/85 italic" data-testid="pup-winner-caption">“{w.caption}”</p>}
                <p className="mt-2 text-sm text-white/60" data-testid="pup-winner-owner">Shared by {w.owner_name}</p>
              </>
            ) : (
              <p className="mt-3 text-white/80">Every month we crown one magnificent Bulldog. The winner stars right here, gets a share-worthy spotlight, and scores free shipping on their next order.</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="pup-stats">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold" style={{ color: "#fff" }} data-testid="pup-entries-count"><Camera className="w-3.5 h-3.5 text-[#FFD93D]" /> {data.entries_count} {data.entries_count === 1 ? "pup" : "pups"} entered{w ? "" : " so far"}</span>
              {data.voting_open ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B9D] px-3 py-1.5 text-xs font-bold" style={{ color: "#fff" }} data-testid="pup-days-left"><Clock className="w-3.5 h-3.5" /> {data.days_left === 0 ? "Last day to vote!" : `${data.days_left} ${data.days_left === 1 ? "day" : "days"} left to vote`}</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFD93D] px-3 py-1.5 text-xs font-bold text-[#19324A]" data-testid="pup-voting-closed"><Trophy className="w-3.5 h-3.5" /> {data.month_label} winner crowned</span>
              )}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/pup-of-the-month" className="btn-pink inline-flex items-center gap-2" data-testid="pup-enter-link"><Camera className="w-5 h-5" /> Enter your pup <ArrowRight className="w-4 h-4" /></Link>
              {w && <div className="bg-white/10 rounded-full p-1"><ShareBar compact title={`${w.pup_name} is The Happy Bulldog Co. Pup of the Month 🏆`} path="/api/pup/share" testid="pup-share" /></div>}
              {w && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pup/${w.id}/share-image.png`} download={`pup-of-the-month-${w.pup_name}.png`} className="inline-flex items-center gap-2 rounded-full border-2 border-[#FFD93D] text-[#FFD93D] px-4 py-2 text-sm font-bold hover:bg-[#FFD93D] hover:text-[#19324A] transition-colors" data-testid="pup-download-card">
                  <Download className="w-4 h-4" /> Download {w.pup_name}'s card
                </a>
              )}
            </div>
            {data.gallery.length > 0 && (
              <div className="mt-6" data-testid="pup-gallery">
                {data.total_votes > 0 && data.voting_open ? (
                  <>
                    <p className="text-xs font-bold text-white/60 mb-2 inline-flex items-center gap-1" data-testid="pup-top-title"><Heart className="w-3.5 h-3.5 fill-[#FF6B9D] text-[#FF6B9D]" /> Most-hearted pups this month</p>
                    <div className="flex flex-wrap gap-2" data-testid="pup-top-list">
                      {data.gallery.filter((e) => e.votes > 0).slice(0, 3).map((e, i) => (
                        <Link key={e.id} to={`/pup-of-the-month?pup=${e.id}`} className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors pl-1 pr-3 py-1" data-testid={`pup-top-${e.id}`}>
                          <span className="relative">
                            <img src={toUrl(e.photo)} alt={e.pup_name} className="w-10 h-10 rounded-full object-cover border-2 border-[#FFD93D]" />
                            <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#FFD93D] text-[#19324A] text-[10px] font-extrabold inline-flex items-center justify-center">{i + 1}</span>
                          </span>
                          <span className="text-sm font-bold" style={{ color: "#fff" }}>{e.pup_name}</span>
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FF6B9D]" data-testid={`pup-top-votes-${e.id}`}><Heart className="w-3 h-3 fill-current" /> {e.votes}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold text-white/60 mb-2">This month's entries so far{data.voting_open ? " — tap to heart your favourite" : ""}</p>
                    <Link to="/pup-of-the-month" className="flex -space-x-2" data-testid="pup-gallery-link">
                      {data.gallery.slice(0, 8).map((e) => <img key={e.id} src={toUrl(e.photo)} alt={e.pup_name} title={e.pup_name} className="w-11 h-11 rounded-full object-cover border-2 border-[#19324A]" />)}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
